import React, { useEffect, useId, useMemo, useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2, Separator } from '@betterment/samba-react';
import { CursorProvider } from '@betterment/shared-components';
import { useDebounce } from 'use-debounce';

import { AccountsFilter } from './account-filter/AccountsFilter';
import { BalanceDetailsSheet } from './balance-details/BalanceDetailsSheet';
import { BalanceChart } from './BalanceChart';
import { BalanceChartData } from './BalanceChartData';
import { UserBalancePerformanceDocument } from './graphql/userPerformanceBalance.generated';

import { DateRangeOption } from '../../../../graphql/schema';
import { PerformanceAccountsQuery } from '../../graphql/performanceAccounts.generated';

export const Balance: React.FC<{
  dateRangeOption: DateRangeOption;
  accounts: PerformanceAccountsQuery['performance']['accounts'];
  initialSelectedIds?: string[];
}> = ({ dateRangeOption, accounts, initialSelectedIds = [] }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(initialSelectedIds));
  const [debounceTimer, setDebounceTimer] = useState(0);

  useEffect(() => {
    // Set debounce timer after initial render to avoid delaying query fired on mount
    setDebounceTimer(1000);
  }, []);

  const [debouncedIds] = useDebounce(selectedIds, debounceTimer);
  const [debouncedDateRange] = useDebounce(dateRangeOption, debounceTimer);

  // Memoize array conversion to avoid unnecessary query re-renders
  const debouncedIdsArray = useMemo(() => Array.from(debouncedIds), [debouncedIds]);

  const describeId = useId();
  const { data, loading } = useGraphQLQuery(UserBalancePerformanceDocument, {
    variables: { dateRangeOption: debouncedDateRange, accountIds: debouncedIdsArray },
    throwOnError: true,
    /*
      Using no-cache here since this query and the one used in the sibling AccountsCard component have cache collisions
      Both update "performance" when the user has only selected one id on the account filer, however they request different attributes
      The mismatch in shape causes the GQL hooks to re-fetch to fix what it thinks is an improperly cached response
      Balance is expected to only show fresh data from the back-end, making requests as the user updates selected ids, while AccountsCard should only make its query on data init
      "no-cache" allows this query to not read or write to the cache, thus avoiding collisions with the AccountsCard query while maintaining expected behavior
    */
    fetchPolicy: 'no-cache',
  });

  let startDate: Date | undefined;
  let endDate: Date | undefined;

  if (data?.performance?.history) {
    const { history } = data.performance;
    if (history.timeSeries.length > 0) {
      startDate = new Date(history.timeSeries[0].date);
      endDate = new Date(history.timeSeries[history.timeSeries.length - 1].date);
    }
  }

  return (
    <Card>
      <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
        <H2 variant="title200">Balance</H2>
        <AccountsFilter accounts={accounts} selectedSetter={setSelectedIds} selectedAccountIds={selectedIds} />
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content css={{ padding: 'space.300' }}>
        <CursorProvider>
          <BalanceChartData totals={data?.performance.totals} dateRangeOption={dateRangeOption} loading={loading} />
          <BalanceChart history={data?.performance.history} balanceDetailsId={describeId} loading={loading} />
        </CursorProvider>
        <BalanceDetailsSheet data={data?.performance.totals} startDate={startDate} endDate={endDate} />
      </Card.Content>
    </Card>
  );
};
